/**
 * @cogo/react-renderer/adapters/schema-v4-page — Phase 485 page tree → React adapter.
 *
 * Consumes a Phase 485 page-synthesis output tree
 * (`{schemaVersion, blockType, root}`) and renders it via the shared
 * `ElementRenderer` tree-walker. This is the D-486-03 reusable replacement
 * for the removed Phase 478 IRI-switch capture adapter — any consumer
 * (the v77 demo, future apps) can render a Phase 485 page through it.
 *
 * `CogoElement` (what `ElementRenderer` consumes) is a strict superset of
 * schema-v4 `$defs/Element` — only the heading-`level` integer is schema-v4-
 * only — so the only structural normalization needed is unwrapping `root`;
 * no deep field-by-field normalizer (it would be dead code — RESEARCH.md
 * §CogoElement vs schema-v4 audit).
 *
 * The rendered tree is wrapped in a design-token base
 * (`getIsolatedAppStyle(themeTokens)`): without it, semantic Tailwind classes
 * like `bg-background` / `text-foreground` resolve to no value because no
 * `:root` CSS variables are defined (RESEARCH.md Pitfall 4). The optional
 * `themeTokens` prop makes that wrap the parameterizable seam Plan 03's
 * SC#3 token-diff test consumes — the same tree rendered with two distinct
 * `themeTokens` values produces visibly different CSS-variable output.
 *
 * Public exports: `<SchemaV4PageRenderer/>` (component), type `SchemaV4Page`,
 * and a re-export of `registerCommerceAliases` so consumers of this adapter
 * sub-path (e.g. the v77 demo's `verify-type-coverage.mjs` build gate) can
 * build the exact renderer registry the adapter uses without reaching into a
 * non-exported module path.
 *
 * Phase 486 — v77 demo design wire.
 */

import { useMemo, type ReactElement } from "react"
import { ElementRenderer } from "../renderers/element-renderer"
import { registerAllRenderers } from "../renderers"
import { ElementRegistry } from "../core/element-registry"
import { RenderContext, type RenderContextValue } from "../core/render-context"
import { getIsolatedAppStyle } from "../runtime/design-tokens"
import type { CogoElement } from "../core/types"
import { registerCommerceAliases } from "./commerce-aliases"

/** Re-exported so the schema-v4-page adapter sub-path is the single registry-build entry. */
export { registerCommerceAliases } from "./commerce-aliases"

/**
 * Phase 485 page-synthesis output tree — the `PageTree` / `PageSynthesisOutput.page`
 * shape (RESEARCH.md Assumption A3).
 *
 * `root` is typed `CogoElement & { level?: number }`: `CogoElement` is a strict
 * superset of schema-v4 `$defs/Element` except for the heading-`level` integer,
 * which the widening admits for TS cleanliness.
 */
export interface SchemaV4Page {
  /** schema-v4 version string, e.g. "4.0". */
  schemaVersion: string
  /** the block type the page was synthesized as. */
  blockType: string
  /** the page UI tree root element. */
  root: CogoElement & { level?: number }
}

/** Optional design-token overrides — the parameterizable SC#3 token-diff seam. */
export type SchemaV4PageThemeTokens = Parameters<typeof getIsolatedAppStyle>[0]

export interface SchemaV4PageRendererProps {
  /** the Phase 485 page tree to render. */
  page: SchemaV4Page
  /**
   * Optional design-token overlay. Defaults to `undefined` →
   * `getIsolatedAppStyle()` emits the full `DEFAULT_LIGHT_THEME` token set.
   * Passing a distinct overlay changes the emitted CSS-variable values —
   * this is the SC#3 token-diff seam.
   */
  themeTokens?: SchemaV4PageThemeTokens
  /** Optional custom event dispatcher for interactive behaviors. */
  dispatchEvent?: (elementId: string, eventType: string, payload?: unknown) => void
  /** Optional custom symbol resolver. */
  resolveSymbol?: (expr: string) => unknown
  /** Optional custom navigation. */
  navigate?: (route: string) => void
  /** Optional rendering mode. */
  mode?: "preview" | "edit"
  /** Optional color scheme (defaults to "light"). */
  colorScheme?: "light" | "dark"
}

/**
 * Render a Phase 485 schema-v4 page tree to React widgets.
 *
 * Wires the shared `ElementRenderer` inside its own `RenderContext.Provider`
 * (mandatory — `ElementRenderer` calls `useRenderContext()` which throws if no
 * provider is mounted above it) AND inside a `getIsolatedAppStyle(themeTokens)`
 * design-token base wrap so the LLM-emitted Tailwind classes resolve.
 *
 * The registry is built fresh per `themeTokens` change via `useMemo`:
 * `registerAllRenderers` (~110 generic registrations) then
 * `registerCommerceAliases` (the 12 commerce-semantic types).
 */
export function SchemaV4PageRenderer({
  page,
  themeTokens,
  dispatchEvent,
  resolveSymbol,
  navigate,
  mode = "preview",
  colorScheme = "light",
}: SchemaV4PageRendererProps): ReactElement {
  const ctx = useMemo<RenderContextValue>(() => {
    const registry = new ElementRegistry()
    registerAllRenderers(registry)
    registerCommerceAliases(registry)
    return {
      registry,
      resolveSymbol: resolveSymbol ?? ((expr: string) => expr),
      dispatchEvent: dispatchEvent ?? (() => {}),
      navigate,
      mode,
    }
  }, [dispatchEvent, resolveSymbol, navigate, mode])

  // Design-token base wrap. `getIsolatedAppStyle()` (no tokens) emits the full
  // DEFAULT_LIGHT_THEME CSS-variable set; an overlay changes the values — the
  // SC#3 seam (RESEARCH.md Pitfall 4 + Plan 03 token-diff test).
  const appStyle = useMemo(() => getIsolatedAppStyle(themeTokens, colorScheme), [themeTokens, colorScheme])

  return (
    <RenderContext.Provider value={ctx}>
      <div data-cogo-schema-v4-page="true" style={appStyle}>
        <ElementRenderer element={page.root} />
      </div>
    </RenderContext.Provider>
  )
}
